import Link from 'next/link'
import React from 'react'
import { useSession } from '@/utils/auth/user-context'
import { LoginButton } from './login-button'
import { RocketIcon } from 'lucide-react'

const ApiDocs: React.FC = () => {
  const session = useSession()

  const codeClassNames = 'bg-gray-200 p-[2px] text-sm text-gray-800'
  const methodClassNames =
    'inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 mr-2'
  const paramClassNames =
    'inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'
  const statusClassNames =
    'inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 mr-1'

  // TODO: localize this component
  return (
    <div className='grid max-w-full grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8'>
      <div>
        <h2 className='text-3xl font-bold leading-7'>API Documentation</h2>
        <p className='mt-1 text-base leading-6 text-gray-700'>
          The Supermeme.ai API provides endpoints for meme generation and
          search. All endpoints require authentication.
        </p>
        <div className='mt-4 space-y-2'>
          <h3 className='text-lg font-semibold'>Authentication</h3>
          <p className='text-base text-gray-700'>
            All API requests require an API key passed in the header:
          </p>
          <pre className='overflow-x-auto rounded-md bg-gray-100 p-2'>
            <code>Authorization: Bearer {'{api-key}'}</code>
          </pre>
        </div>
      </div>

      {session?.user ? (
        <div className='mt-4'>
          <p className='text-base text-gray-700'>
            You can find your API key in the{' '}
            <Link
              href='/account/developers'
              className='text-indigo-600 underline hover:text-indigo-500'
            >
              Developer Settings
            </Link>{' '}
            section of your account.
          </p>
        </div>
      ) : (
        <div className='mt-4 space-y-2'>
          <p className='text-base text-gray-700'>
            Please sign in to get your API key.
          </p>
          <LoginButton
            buttonText='Sign In'
            buttonIcon={<RocketIcon className='mr-2 h-4 w-4' />}
            className='col-span-4 w-full lg:w-auto'
          />
        </div>
      )}

      <div className='grid max-w-full grid-cols-1 gap-x-8 gap-y-10 py-8 md:col-span-3 md:grid-cols-3'>
        <div className='space-y-4'>
          <div className='flex items-center'>
            <span className={methodClassNames}>POST</span>
            <h3 className='text-lg font-semibold leading-7'>
              /api/v2/meme/image
            </h3>
          </div>
          <p className='text-base font-medium leading-6 text-gray-700'>
            Generate memes with captions overlaid on images
          </p>
          <p className='text-base leading-6 text-gray-500'>
            Returns a list of temporary image URLs with captions already
            applied. <b>URLs are valid for 1 hour</b>. Ideal for direct use in
            applications where caption editing isn&apos;t needed.
          </p>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Request Parameters</h4>
            <div className='space-y-3'>
              <div>
                <span className={paramClassNames}>text</span>
                <span className='ml-2 font-medium text-gray-800'>string</span>
                <span className='ml-2 text-red-500'>required</span>
                <p className='mt-1 text-gray-600'>
                  Text prompt to generate memes (max 300 characters). Supports
                  any Latin-based language.
                </p>
              </div>

              <div>
                <span className={paramClassNames}>count</span>
                <span className='ml-2 font-medium text-gray-800'>integer</span>
                <span className='ml-2 text-gray-500'>optional</span>
                <p className='mt-1 text-gray-600'>
                  Number of memes to return (max 12). Fewer memes results in
                  faster responses.
                </p>
              </div>
            </div>
          </div>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Response</h4>
            <div>
              <span className={statusClassNames}>200 OK</span>
              <p className='mt-1 text-gray-600'>
                Returns an array of meme objects with captions and image URLs.
              </p>
            </div>
          </div>

          <div className='mt-2'>
            <h4 className='text-md mb-2 font-medium'>Notes</h4>
            <ul className='list-disc space-y-1 pl-5 text-gray-600'>
              <li>Language is automatically detected from input text</li>
              <li>Generated captions match the detected language</li>
            </ul>
          </div>
        </div>

        <div className='md:col-span-2'>
          <div className='grid grid-cols-1 gap-y-6'>
            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Request</h4>
              <div className='mb-4 rounded-md bg-gray-100 p-3'>
                <p className='mb-1 text-sm font-medium text-gray-700'>cURL</p>
                <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                  <code>
                    {`curl --request POST \

--url https://app.supermeme.ai/api/v2/meme/image \

--header 'Authorization: Bearer {api-key}' \

--header 'Content-Type: application/json' \

--data '\{
  "text": "text to generate memes with",
  "count": 6
}'`}
                  </code>
                </pre>
              </div>

              <div className='mb-2 rounded-md bg-gray-100 p-3'>
                <p className='mb-1 text-sm font-medium text-gray-700'>
                  JavaScript
                </p>
                <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                  <code>
                    {`const response = await fetch('https://app.supermeme.ai/api/v2/meme/image', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer {api-key}',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    text: 'text to generate memes with',
    count: 6
  })
});`}
                  </code>
                </pre>
              </div>
            </div>

            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Response</h4>
              <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                <code>
                  {`
{
  "memes": [
    "https://temp.url.co/storage/v1/object/memes-main/name-of-image-1.png",
    "https://temp.url.co/storage/v1/object/memes-main/name-of-image-2.png",
    "https://temp.url.co/storage/v1/object/memes-main/name-of-image-3.png",
    // ... more images
  ]
}
                        `}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className='grid max-w-full grid-cols-1 gap-x-8 gap-y-10 py-8 md:col-span-3 md:grid-cols-3'>
        <div className='space-y-4'>
          <div className='flex items-center'>
            <span className={methodClassNames}>POST</span>
            <h3 className='text-lg font-semibold leading-7'>
              /api/v1/meme/text
            </h3>
          </div>
          <p className='text-base font-medium leading-6 text-gray-700'>
            Text-to-meme - separate captions and images
          </p>
          <p className='text-base leading-6 text-gray-500'>
            This endpoint returns memes with captions and images{' '}
            <b>separately</b>. The images don&apos;t have text on them, giving
            you flexibility to edit captions or use custom fonts and formatting
            to generate the final meme image at the consumer end.
          </p>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Request Parameters</h4>
            <div className='space-y-3'>
              <div>
                <span className={paramClassNames}>text</span>
                <span className='ml-2 font-medium text-gray-800'>string</span>
                <span className='ml-2 text-red-500'>required</span>
                <p className='mt-1 text-gray-600'>
                  Text prompt to generate memes (max 300 characters). Supports
                  any of the 110+ supported languages.
                </p>
              </div>
            </div>
          </div>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Response</h4>
            <div>
              <span className={statusClassNames}>200 OK</span>
              <p className='mt-1 text-gray-600'>
                Returns memes with separate captions and image paths.
              </p>
            </div>
          </div>

          <div className='mt-2'>
            <h4 className='text-md mb-2 font-medium'>Notes</h4>
            <ul className='list-disc space-y-1 pl-5 text-gray-600'>
              <li>Language is automatically detected from input text</li>
              <li>Generated captions match the detected language</li>
              <li>
                The <code className={codeClassNames}>text</code> value can be in
                any of the
                <Link
                  href='/search-supported-languages'
                  className='text-indigo-600 underline hover:text-indigo-500'
                >
                  {' '}
                  110+ supported languages
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className='md:col-span-2'>
          <div className='grid grid-cols-1 gap-y-6'>
            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Request</h4>

              <div className='mb-4 rounded-md bg-gray-100 p-3'>
                <p className='mb-1 text-sm font-medium text-gray-700'>cURL</p>
                <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                  <code>
                    {`curl --request POST \

--url https://app.supermeme.ai/api/v1/meme/text \

--header 'Authorization: Bearer {api-key}' \

--header 'Content-Type: application/json' \

--data '\{
  "text": "text to generate memes with"
}'`}
                  </code>
                </pre>
              </div>
            </div>

            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Response</h4>
              <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                <code>
                  {`{
  "memes": [
    {
      "caption": "When you decide to create some amazing memes",
      "image": "Excited-Minions.png"
    },
    {
      "caption": "When you launch an app and memes are involved",
      "image": "excited-kid.png"
    },
    // ... more memes
  ],
  "searchEmotion": "Happiness",
  "originalText": "text to generate memes with",
  "generatedCaptions": [
    "When you decide to create some amazing memes ",
    "When you launch an app and memes are involved",
    // ... more captions
  ],
  "languageCode": "en"
}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Search Endpoint Documentation */}
      <div className='grid max-w-full grid-cols-1 gap-x-8 gap-y-10 py-8 md:col-span-3 md:grid-cols-3'>
        <div className='space-y-4'>
          <div className='flex items-center'>
            <span className={methodClassNames}>GET</span>
            <h3 className='text-lg font-semibold leading-7'>/api/v1/search</h3>
          </div>
          <p className='text-base font-medium leading-6 text-gray-700'>
            Search Meme Templates
          </p>
          <p className='text-base leading-6 text-gray-500'>
            This endpoint allows you to search for meme templates based on a
            query string. It returns a list of template image paths that match
            your search criteria.
          </p>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Request Parameters</h4>
            <div className='space-y-3'>
              <div>
                <span className={paramClassNames}>query</span>
                <span className='ml-2 font-medium text-gray-800'>string</span>
                <span className='ml-2 text-red-500'>required</span>
                <p className='mt-1 text-gray-600'>
                  Search term to find matching meme templates.
                </p>
              </div>
            </div>
          </div>

          <div className='mt-4'>
            <h4 className='text-md mb-2 font-medium'>Response</h4>
            <div>
              <span className={statusClassNames}>200 OK</span>
              <p className='mt-1 text-gray-600'>
                Returns a list of template image paths that match the search
                criteria.
              </p>
            </div>
          </div>
        </div>

        <div className='md:col-span-2'>
          <div className='grid grid-cols-1 gap-y-6'>
            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Request</h4>

              <div className='mb-4 rounded-md bg-gray-100 p-3'>
                <p className='mb-1 text-sm font-medium text-gray-700'>cURL</p>
                <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                  <code>
                    {`curl --request GET \

--url https://app.supermeme.ai/api/v1/search?query=cat \

--header 'Authorization: Bearer {api-key}'`}
                  </code>
                </pre>
              </div>
            </div>

            <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
              <h4 className='text-md mb-3 font-medium'>Example Response</h4>
              <pre className='overflow-x-auto rounded-md bg-gray-800 p-3 text-sm text-gray-100'>
                <code>
                  {`{
  "templates": [
    "cat-meme-1.png",
    "grumpy-cat.png",
    "surprised-cat.png"
    // ... more templates
  ]
}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApiDocs
